// =============================================================================
// ai-zippy-child — global frontend behaviours
// =============================================================================
// Owns every row of `docs/build/ui-spec.md` §6 / `docs/build/block-map.md` §5
// whose "Owner" column says **child.js**.  Anything assigned to a block's
// `view.js`, to the parent theme, to WooCommerce or to plain CSS is NOT here.
//
//   §6 row  Behaviour                                   Implementation
//   ------  ------------------------------------------    ----------------------
//    1      `.nav-ham` click → toggle drawer              onDrawerToggle()
//    2      `.drawer-backdrop` click → close               setDrawer(false)
//    3      `.drawer-close` click → close                  setDrawer(false)
//    4/5    `.drawer-subtoggle` → toggle `#drawerSub` /
//           `#drawerShopSub`                               toggleSub()
//    13     add to cart → toast "{name} added to cart"    onAddedToCart()
//    14     contact form submit → AJAX + toast + reset    onContactSubmit()
//    15     `#memPopup` auto-show after `popupDelayMs`    schedulePopup()
//    16     popup close / "Maybe later"                    closePopup()
//    17     popup claim → close + WhatsApp tab   closePopup() + native <a> + [data-toast]
//    18     `toast(msg)` auto-hide after `toastMs`        toast() → window.ksToast
//    19     "Jump to FAQs" → smooth scroll to `#faqSection` onInPageAnchor()
//    20/21  WhatsApp / Book Now CTAs → new tab + toast     native <a> + [data-toast]
//
//   §6 rows 6, 22, 23 are CSS-only; rows 7–12 belong to block `view.js`,
//   the parent theme or WooCommerce.
//
// Contract for S3 (block render.php markup):
//   * Toast trigger  — any element with `data-toast="Message"` (a real `href`
//     is kept so the link works without JS; `data-toast` only adds feedback).
//   * Contact form   — add `data-ks-contact` to the `<form>`, give every field
//     a `name`, and add the honeypot `<input name="ks_hp" class="ks-hp">`.
//     `.field-col > form` is matched too, as a fallback for the mockup markup.
//     Optional `data-success-toast="…"` overrides the default success copy.
//   * Add to cart    — keep Woo's own classes and put the product name in
//     `data-ks-product-name` (or `data-name`) so the toast can quote it.
//
// No jQuery, no framework, no build-time dependencies.  ES2020+, one bundle,
// loaded with `defer` + `type="module"` (see `functions.php`).
// =============================================================================

/** Localised by `functions.php` → `wp_localize_script( …, 'ksFrontend', … )`. */
const CFG = window.ksFrontend || {};

const DEFAULT_TOAST_MS = 2600; // ui-spec §6 row 18 — the mockup's `toastMs`
const DEFAULT_SUCCESS = "Enquiry sent — we'll reply on WhatsApp shortly."; // block-map §4.9
const DEFAULT_FAILURE = "Sorry, your enquiry did not send. Please WhatsApp us on 9776 9778.";
const PENDING_ADD_KEY = "ksPendingAdd";

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

/**
 * `wp_localize_script()` stringifies every scalar, so a PHP `(bool) true`
 * reaches the browser as `"1"`.  Accept both shapes rather than trusting one.
 */
const isTrue = (value) => value === true || value === 1 || value === "1" || value === "true";

const toInt = (value, fallback) => {
	const n = parseInt(value, 10);
	return Number.isFinite(n) && n >= 0 ? n : fallback;
};

const reduceMotion = () =>
	window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// =============================================================================
// 1. Toast — §6 row 18.  Single site-wide node (`#toast` in parts/footer.html).
// =============================================================================

let toastTimer = null;

/**
 * Show `.toast` with `message` and auto-hide it after `toastMs`.
 * `toastMs` is read from `#toast[data-toast-ms]` — never hardcoded per call —
 * so the `site-overlays` block attribute stays the single source of truth.
 *
 * @param {string} message
 * @param {number} [ms]  Optional override.
 */
function toast(message, ms) {
	const node = document.getElementById("toast");
	if (!node) return;

	const label = document.getElementById("toastMsg");
	if (label) label.textContent = String(message ?? "");

	const duration = toInt(ms ?? node.dataset.toastMs, DEFAULT_TOAST_MS);

	// Restart the timer rather than stacking it: rapid taps (Book Now, then a
	// cart add) must not leave the toast visible for the sum of both.
	clearTimeout(toastTimer);
	node.classList.add("show");
	toastTimer = setTimeout(() => node.classList.remove("show"), duration);
}

// Blocks' `view.js` and the Woo skin both need to raise a toast; expose one
// global instead of letting each partial re-implement the timer.
window.ksToast = toast;

// =============================================================================
// 2. Mobile drawer — §6 rows 1–3, plus ESC and click-outside.
// =============================================================================

const drawerNodes = () => ({
	drawer: document.getElementById("mobileDrawer"),
	backdrop: document.getElementById("drawerBackdrop"),
	ham: document.getElementById("navHam"),
});

const isDrawerOpen = () => {
	const { drawer } = drawerNodes();
	return !!drawer && drawer.classList.contains("open");
};

/**
 * Toggle `.open` on drawer + backdrop + hamburger exactly as the mockup's
 * `toggleDrawer()` / `closeDrawer()` did, and keep `aria-expanded` in sync.
 */
function setDrawer(open) {
	const { drawer, backdrop, ham } = drawerNodes();
	[drawer, backdrop, ham].forEach((node) => node && node.classList.toggle("open", open));
	if (ham) ham.setAttribute("aria-expanded", open ? "true" : "false");

	// Re-opening must start from the mockup's collapsed state, so drop the
	// Treatments / Shop sub-menus whenever the drawer goes away.
	if (!open) {
		$$("[data-ks-subtoggle]").forEach((trigger) => {
			trigger.setAttribute("aria-expanded", "false");
			const panel = document.getElementById(trigger.getAttribute("aria-controls") || "");
			if (panel) panel.classList.remove("open");
		});
	}
}

const toggleDrawer = () => setDrawer(!isDrawerOpen());

// §6 rows 4–5.  The triggers are real `<a href>`s (no-JS fallback: the browser
// navigates to the archive); with JS on they behave as the mockup's disclosure.
function toggleSub(trigger) {
	const panel = document.getElementById(trigger.getAttribute("aria-controls") || "");
	if (!panel) return;
	const open = !panel.classList.contains("open");
	panel.classList.toggle("open", open);
	trigger.setAttribute("aria-expanded", open ? "true" : "false");
}

// =============================================================================
// 3. Mega-menu dropdowns — §6 row 6 is CSS `:hover` / `:focus-within`.
//    A touch device has neither, and tablets in landscape (>1000px) still get
//    `.nav-links` instead of the drawer, so `(hover: none)` needs a tap-toggle.
//    `.nav-item.open` in `sections/_site-header.scss` mirrors the hover state.
// =============================================================================

const hasHover = () =>
	!(window.matchMedia && window.matchMedia("(hover: none)").matches);

function closeAllDropdowns(except) {
	$$(".nav-item.open").forEach((item) => {
		if (item !== except) item.classList.remove("open");
	});
}

function onNavHeadClick(event, head) {
	if (hasHover()) return; // desktop: CSS owns it, do not fight `:hover`
	event.preventDefault();
	const item = head.closest(".nav-item");
	if (!item) return;
	const open = !item.classList.contains("open");
	closeAllDropdowns(item);
	item.classList.toggle("open", open);
}

// =============================================================================
// 4. Membership popup — §6 rows 15–17.
// =============================================================================

function closePopup() {
	const popup = document.getElementById("memPopup");
	if (popup) popup.classList.remove("show");
}

/**
 * `popupDelayMs` / `popupHomeOnly` are read from the backdrop's `data-*`
 * attributes, which `parts/footer.html` emits from the `site-overlays` block
 * attributes — so the numbers stay editable in the block inspector.
 */
function schedulePopup() {
	const popup = document.getElementById("memPopup");
	if (!popup) return;

	const homeOnly = String(popup.dataset.popupHomeOnly ?? "true") !== "false";
	const isHome = isTrue(CFG.isFrontPage) || document.body.classList.contains("home");
	if (homeOnly && !isHome) return;

	const delay = toInt(popup.dataset.popupDelayMs, 3500);
	setTimeout(() => popup.classList.add("show"), delay);
}

// =============================================================================
// 5. "Added to cart" toast — §6 row 13 / block-map §5 row 14.
// =============================================================================

const ADDToSelector =
	'.woo-add, .az-add-to-cart, .ajax_add_to_cart, .single_add_to_cart_button, a[href*="add-to-cart="]';

/** Read the product name the way the mockup's `addToCart(name)` received it. */
const productNameOf = (button) => {
	const card = button.closest("[data-name]") || button.closest(".woo-card") || button;
	return (
		button.dataset.ksProductName ||
		button.dataset.name ||
		card.dataset?.name ||
		$(".woo-name", card)?.textContent?.trim() ||
		button.getAttribute("aria-label") ||
		"Product"
	);
};

/**
 * Remember which button was clicked so the toast can quote the product name
 * after the (async) cart write completes.  `sessionStorage` covers the classic
 * non-AJAX path, where the add-to-cart POST reloads the whole page.
 */
function rememberAdd(button) {
	const name = productNameOf(button);
	try {
		sessionStorage.setItem(PENDING_ADD_KEY, name);
	} catch {
		/* private mode — the in-memory copy below still covers the AJAX path */
	}
	return name;
}

const takePendingAdd = () => {
	let name = "";
	try {
		name = sessionStorage.getItem(PENDING_ADD_KEY) || "";
		sessionStorage.removeItem(PENDING_ADD_KEY);
	} catch {
		/* ignore */
	}
	return name;
};

let lastAddName = "";

/**
 * One toast per add-to-cart, whichever transport reported it.
 *
 * The parent already raises its own `.az-toast` when the click went through
 * `.az-add-to-cart`; in that case we stay quiet rather than stacking two
 * notifications for one action.
 */
function raiseAddToast() {
	const name = lastAddName || takePendingAdd() || "Product";
	lastAddName = "";
	if ($(".az-toast--success")) return;
	toast(`${name} added to cart`);
}

/** Timestamp of the last native `wc-blocks_added_to_cart` (see below). */
let lastNativeAddAt = 0;

/**
 * Native transport — the parent theme's Store API path
 * (`modules/add-to-cart.js`, used by the PDP buy box `.az-add-to-cart`).
 */
function onAddedToCart() {
	lastNativeAddAt = Date.now();
	raiseAddToast();
}

/**
 * jQuery transport — WooCommerce core's ARCHIVE handler
 * (`assets/js/frontend/add-to-cart.min.js`), which is what the shop grid's
 * `.woo-add.ajax_add_to_cart` buttons go through.  Core triggers `added_to_cart`
 * via jQuery only, and jQuery's `.trigger()` does not dispatch a native DOM
 * event, so the listener above never saw the archive adds: the grid added the
 * product and refreshed the mini-cart but stayed silent (block-map §5 row 14,
 * carried forward as BUILD-STATE "Worker D note (a)").
 *
 * The PDP path fires BOTH events back-to-back in the same tick, so the jQuery
 * half is suppressed for a beat after a native one — one click, one toast.
 */
function onAddedToCartLegacy() {
	if (Date.now() - lastNativeAddAt < 1500) return;
	raiseAddToast();
}

/**
 * Non-AJAX fallback: a classic `?add-to-cart=` POST reloads the page, so no
 * event ever fires.  If Woo then printed a success notice, honour the name we
 * stashed before the reload and toast it once.
 */
function toastPendingAddOnLoad() {
	const name = takePendingAdd();
	if (!name) return;
	const notice = $(".woocommerce-message");
	if (!notice || !/added to your cart|view cart/i.test(notice.textContent || "")) return;
	toast(`${name} added to cart`);
}

// =============================================================================
// 6. In-page anchors — §6 row 19 ("Jump to FAQs" → `#faqSection`).
// =============================================================================

/** Sticky-header offset, read from the `--nav-h` token (ui-spec §3). */
function navOffset() {
	const raw = getComputedStyle(document.documentElement).getPropertyValue("--nav-h");
	const n = parseFloat(raw);
	return Number.isFinite(n) ? n : 0;
}

function scrollToTarget(target) {
	const top = target.getBoundingClientRect().top + window.scrollY - navOffset();
	window.scrollTo({ top: Math.max(top, 0), behavior: reduceMotion() ? "auto" : "smooth" });
}

// =============================================================================
// 7. Contact form — §6 row 14 / decision 4 / block-map §1.8.
// =============================================================================

const CONTACT_FORM_SELECTOR = "form[data-ks-contact], .field-col > form";

const setBusy = (button, busy) => {
	if (!button) return;
	button.disabled = busy;
	button.classList.toggle("is-busy", busy);
};

/**
 * Client half of the enquiry form.  The PHP half is the `ks_contact_submit`
 * (alias `ks_contact`) AJAX callback in `functions.php` — a stub until S3
 * replaces its body with the nonce check + honeypot + `wp_mail()`.
 *
 * The mockup markup carries no `name` attributes; S3's `contact-panel`
 * render.php adds them.  `FormData(form)` is posted verbatim, so the handler
 * receives exactly the field names the block emits.
 */
async function onContactSubmit(form) {
	// Honeypot: a filled value means a bot.  Abort silently — no toast, no
	// network — exactly as a honeypot should behave.  S3 adds the field.
	const honeypot = $('[name="ks_hp"], .ks-hp, [data-ks-honeypot]', form);
	if (honeypot && String(honeypot.value || "").trim() !== "") return;

	// Native validation already ran (the browser fires `submit` only when the
	// `required` fields are filled), so this point is reached with valid input.
	const data = new FormData(form);

	// `action` / `nonce` are appended only if the markup did not carry them:
	// block-map §4.9 says the form posts `action=ks_contact`, while the S2 brief
	// names `ks_contact_submit`.  `functions.php` registers both slugs against
	// the same callback, so either value works and a duplicate key is avoided.
	if (!data.has("action")) data.append("action", CFG.action || "ks_contact_submit");
	if (!data.has("nonce")) data.append("nonce", CFG.nonce || "");

	const submit = $('[type="submit"]', form);
	setBusy(submit, true);

	try {
		const response = await fetch(CFG.ajaxUrl || "/wp-admin/admin-ajax.php", {
			method: "POST",
			credentials: "same-origin",
			body: data,
		});
		const json = await response.json().catch(() => null);
		const message = json?.data?.message;

		if (response.ok && json?.success) {
			toast(form.dataset.successToast || message || DEFAULT_SUCCESS);
			form.reset();
		} else {
			toast(message || DEFAULT_FAILURE);
		}
	} catch {
		// Network failure / non-JSON response — still tell the visitor something.
		toast(DEFAULT_FAILURE);
	} finally {
		setBusy(submit, false);
	}
}

// =============================================================================
// 8. Delegated listeners — one per event type, so blocks rendered later
//    (or swapped in by WooCommerce fragments) work without re-binding.
// =============================================================================

function onClick(event) {
	const target = event.target;
	if (!(target instanceof Element)) return;

	// --- drawer -------------------------------------------------------------
	if (target.closest("[data-ks-drawer-toggle]")) {
		event.preventDefault();
		toggleDrawer();
		return;
	}
	if (target.closest("[data-ks-drawer-close]")) {
		event.preventDefault();
		setDrawer(false);
		return;
	}
	// Rows 4–5.  Handled before the generic "link closes the drawer" rule below,
	// otherwise tapping "Treatments" would navigate away instead of disclosing.
	const sub = target.closest("[data-ks-subtoggle]");
	if (sub) {
		event.preventDefault();
		toggleSub(sub);
		return;
	}

	// --- membership popup ---------------------------------------------------
	if (target.closest("[data-ks-popup-close]")) {
		event.preventDefault();
		closePopup();
		return;
	}
	// Row 17: the claim link closes the popup and opens WhatsApp in a new tab
	// (native <a>, no preventDefault); its `data-toast` (handled further down)
	// supplies the feedback message, so the copy stays editable in the block
	// inspector instead of duplicated here.
	if (target.closest("[data-ks-popup-claim]")) {
		closePopup();
	}

	// --- mega-menu (touch only) ---------------------------------------------
	const head = target.closest(".nav-dd-head");
	if (head) onNavHeadClick(event, head);

	// --- in-page anchors (row 19) -------------------------------------------
	const anchor = target.closest('a[href^="#"]');
	if (anchor) {
		const id = anchor.getAttribute("href") || "";
		const dest = id.length > 1 ? document.getElementById(id.slice(1)) : null;
		if (dest) {
			event.preventDefault();
			setDrawer(false);
			closeAllDropdowns(null);
			scrollToTarget(dest);
			return;
		}
	}

	// --- add to cart (row 13) -----------------------------------------------
	const addBtn = target.closest(ADDToSelector);
	if (addBtn) {
		lastAddName = rememberAdd(addBtn);
	}

	// --- toast triggers (rows 18, 20, 21) -----------------------------------
	// Last, and deliberately without `preventDefault()`: WhatsApp / Book Now
	// CTAs keep their real `href`, so the tab still opens with JS disabled and
	// the toast is pure additive feedback.
	const toasted = target.closest("[data-toast]");
	if (toasted) toast(toasted.dataset.toast);

	// --- a real navigation from inside the drawer closes it ------------------
	// (`navFromDrawer(page)` in the mockup; here the href does the navigating.)
	if (isDrawerOpen() && target.closest(".mobile-drawer a")) setDrawer(false);
}

function onKeydown(event) {
	if (event.key !== "Escape") return;
	if (isDrawerOpen()) setDrawer(false);
	const openItem = $(".nav-item.open");
	if (openItem) openItem.classList.remove("open");
	closePopup();
}

function onSubmit(event) {
	const form = event.target instanceof Element ? event.target.closest(CONTACT_FORM_SELECTOR) : null;
	if (!form) return;
	// Row 14: the mockup `preventDefault()`s.  We keep that, but replace the
	// fake success with a real AJAX round-trip.
	event.preventDefault();
	onContactSubmit(form);
}

function onFocusIn(event) {
	// Click-outside parity for the touch dropdowns: focusing anything outside
	// the open `.nav-item` closes it, so keyboard users get the same behaviour.
	if (!(event.target instanceof Element)) return;
	if (event.target.closest(".nav-item")) return;
	closeAllDropdowns(null);
}

function init() {
	document.addEventListener("click", onClick);
	document.addEventListener("keydown", onKeydown);
	document.addEventListener("submit", onSubmit);
	document.addEventListener("focusin", onFocusIn);
	document.body.addEventListener("wc-blocks_added_to_cart", onAddedToCart);
	// Archive AJAX adds only fire the jQuery event — see onAddedToCartLegacy().
	if (window.jQuery) {
		window.jQuery(document.body).on("added_to_cart", onAddedToCartLegacy);
	}

	schedulePopup();
	toastPendingAddOnLoad();
}

// The bundle is a deferred module, so it normally runs after parsing — but guard
// anyway: a `defer`ed classic script on a cached page can still land early.
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
	init();
}
